import { useMutation } from '@apollo/client'
import { Alert, ButtonLink } from '@designsforhealth/dfh-react-components'
import { CircularProgress, Typography } from '@material-ui/core'
import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'

import {
  CartFragment,
  RemoveCouponFromCartDocument,
  SetDispensaryPromoCodeOnCartDocument,
} from '../../../graphql/magento'
import useIsMounted from '../../../hooks/useIsMounted'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../layouts/context'
import { CartDispensaryPromoCode } from '../../../lib/cart/dispensary-promo'
import { usePromoCodeUtilities } from '../../../lib/cart/hooks/usePromoCodeUtilities'
import { logAndCaptureException } from '../../../utils/errorTools'
import { checkAndDiscardQueryResponse } from '../../../utils/graphqlTools'
import ErrorUpdatingCartMessage from '../../ErrorUpdatingCartMessage'

const StyledHeader = styled.header`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 10px;
`

const StyledTitle = styled(Typography)`
  && {
    margin-bottom: 0;
  }
`

const StyledButtonLink = styled(ButtonLink)`
  text-decoration: underline;
`

const StyledCouponCode = styled.div`
  display: block;
  width: 100%;
  margin-top: 10px;
  font-size: 12px;
  line-height: 14px;
`

export interface AppliedPromoCodeProps {
  appliedCoupons: CartFragment['applied_coupons']
  cartDispensaryPromoCodes: CartDispensaryPromoCode[] | undefined
  cartId: string
  className?: string
}

const AppliedPromoCode: React.FC<AppliedPromoCodeProps> = ({
  appliedCoupons,
  cartDispensaryPromoCodes,
  cartId,
  className,
}) => {
  const token = useContext(TokenContext)
  const [removeCoupon, { loading: removeCouponsLoading, error: removeCouponsError }] = useMutation(
    RemoveCouponFromCartDocument,
    {
      context: { token },
    },
  )
  useLogAndCaptureError(removeCouponsError)

  const [
    setDispensaryPromoCode,
    { loading: setDispensaryPromoCodeLoading, error: setDispensaryPromoCodeError },
  ] = useMutation(SetDispensaryPromoCodeOnCartDocument, {
    context: { token },
  })
  useLogAndCaptureError(setDispensaryPromoCodeError)

  const { concealCode, loading: utilitiesLoading } = usePromoCodeUtilities({
    cartDispensaryPromoCodes,
  })
  const [concealedCodes, setConcealedCodes] = useState<string[]>([])
  const isMounted = useIsMounted()

  useEffect(() => {
    if (!appliedCoupons || utilitiesLoading) {
      setConcealedCodes([])
      return
    }
    appliedCoupons
      .reduce(async (acc: Promise<string[]>, coupon) => {
        if (!coupon) return acc
        const awaitedAcc = await acc
        awaitedAcc.push(await concealCode(coupon.code))
        return awaitedAcc
      }, Promise.resolve([]))
      .then((value) => {
        if (isMounted.current) {
          setConcealedCodes(value)
        }
      })
      .catch(logAndCaptureException)
  }, [appliedCoupons, concealCode, isMounted, utilitiesLoading])

  // TODO: For removal of individual coupons, pass 'couponCode' parameter to function and variable.
  //  The GraphQL schema and resolver have already been updated to support this functionality
  //  Pass the couponCode in the onClick property of the button below: onClick={() => handleRemoveCoupon(coupon.code)}
  //  Move the button component inside the map of appliedCoupons below
  const handleRemoveAllCoupons = () => {
    removeCoupon({
      variables: { cartId },
    })
      .then(checkAndDiscardQueryResponse)
      .then(() =>
        setDispensaryPromoCode({
          variables: { cartId, dispensaryPromoCode: '' },
        }),
      )
      .catch(() => {
        // no-op
      })
  }

  return (
    <div className={className}>
      <StyledHeader>
        <StyledTitle variant="h5">APPLIED PROMO</StyledTitle>
        <StyledButtonLink
          disabled={removeCouponsLoading || setDispensaryPromoCodeLoading}
          onClick={handleRemoveAllCoupons}
        >
          {removeCouponsLoading || setDispensaryPromoCodeLoading ? (
            <CircularProgress size={16} />
          ) : (
            'Remove All'
          )}
        </StyledButtonLink>
      </StyledHeader>
      {concealedCodes.map((text) => (
        <StyledCouponCode key={text}>{text}</StyledCouponCode>
      ))}
      {removeCouponsError && (
        <Alert type="error">
          <ErrorUpdatingCartMessage />
        </Alert>
      )}
    </div>
  )
}

const StyledAppliedPromoCode = styled(AppliedPromoCode)`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  font-size: 14px;
  margin-bottom: 15px;
`

export default StyledAppliedPromoCode
